import React from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

export interface RowsDrawerProps {
  /** The card title is the reader-facing name for this table and can be changed in Details. */
  title?: string;
  /** The card's own result columns, for the summary line. */
  columns: ReadonlyArray<string>;
  /** The full row count behind the table, when the backend can report it. */
  rowCount?: number;
  /** True once the reader has expanded this card past the strip. */
  expanded: boolean;
  onToggle: () => void;
}

/**
 * The collapsed face of a table card: a slim strip standing in for the raw
 * grid, so a board reads as a briefing rather than a data dump until a
 * reader asks to see the rows. `LeLensPage` swaps this for the real table
 * card and back by shrinking and restoring the grid frame it sits in; this
 * component only draws the strip itself.
 */
export function RowsDrawer({ columns, expanded, onToggle, rowCount, title }: RowsDrawerProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const label = title?.trim() || 'Table';
  const meta = [
    ...columns,
    rowCount === undefined ? undefined : `${rowCount.toLocaleString('en-AU')} ${rowCount === 1 ? 'row' : 'rows'}`,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' · ');

  return (
    <ButtonBase
      aria-expanded={expanded}
      aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
      onClick={onToggle}
      sx={{
        alignItems: 'center',
        backgroundColor: tokens.surface,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadius}px`,
        display: 'flex',
        height: '100%',
        justifyContent: 'space-between',
        px: 2.25,
        width: '100%',
        '&:hover': { borderColor: alpha(tokens.accent, 0.4) },
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25, minWidth: 0 }}>
        <ExpandMoreIcon
          sx={{
            color: tokens.accent,
            flex: 'none',
            fontSize: 18,
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 150ms ease',
          }}
        />
        <Typography
          sx={{
            color: theme.palette.text.primary,
            flex: '0 1 auto',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          variant="subtitle2"
        >
          {label}
        </Typography>
        {meta && (
          <Typography
            sx={{
              color: alpha(tokens.accent, 0.72),
              flex: '0 1000 auto',
              fontSize: '0.75rem',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {meta}
          </Typography>
        )}
      </Box>
      <Typography sx={{ color: tokens.accent, flex: 'none', fontSize: '0.75rem', fontWeight: 600 }}>
        {expanded ? 'Collapse' : 'Expand'}
      </Typography>
    </ButtonBase>
  );
}

export default RowsDrawer;
